import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  applicationsSelector,
  getAllApplications,
  saveValue,
} from "../store/slices/applicationsSlice";
import moment from "moment";
import { useEffect } from "react";
import { userSelector } from "src/store/slices/userSlice";
import { useNavigate } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import { userTypes } from "src/constants";

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
};

const brokerColumns = [
  {
    field: "applicationId",
    headerName: "ID",
    type: "string",
    width: 100,
  },
  {
    field: "startDate",
    headerName: "Date",
    type: "string",
    width: 100,
  },
  {
    field: "time",
    headerName: "Time",
    type: "string",
    width: 100,
  },

  {
    field: "status",
    headerName: "Status",
    width: 110,
  },
  {
    field: "entityName",
    headerName: "Entity",
    width: 250,
  },
  {
    field: "firstName",
    headerName: "First name",
    width: 150,
  },
  {
    field: "lastName",
    headerName: "Last name",
    width: 150,
  },
  {
    field: "asset",
    headerName: "Asset",
    type: "string",
    width: 280,
  },
  {
    field: "loanAmount",
    headerName: "Loan amount",
    type: "string",
    width: 150,
  },
];

const masterColumns = [
  {
    field: "applicationId",
    headerName: "ID",
    type: "string",
    width: 80,
  },
  {
    field: "startDate",
    headerName: "Date",
    type: "string",
    width: 100,
  },
  {
    field: "time",
    headerName: "Time",
    type: "string",
    width: 100,
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
  },
  {
    field: "entityName",
    headerName: "Entity",
    width: 250,
  },
  {
    field: "brokerOrganisation",
    headerName: "Broker Organisation",
    width: 180,
  },
  {
    field: "brokerContact",
    headerName: "Broker contact",
    width: 180,
  },
  {
    field: "asset",
    headerName: "Asset",
    type: "string",
    width: 280,
  },
  {
    field: "loanAmount",
    headerName: "Loan amount",
    type: "string",
    width: 150,
  },
];

export default function ApplicationsList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const response = useSelector(applicationsSelector);

  const applications = response.applications;
  const { isLoading, page } = response;
  const [rows, setRows] = useState([]);

  const handlePageChange = (newPage) => {
    console.log({ newPage });
    handleGetAllApplications(user, newPage + 1);
    dispatch(saveValue({ page: newPage }));
  };

  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCountState` from being undefined during the loading
  const [rowCountState, setRowCountState] = React.useState(
    applications?.totalDocs || 0
  );

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      applications?.totalDocs !== undefined
        ? applications?.totalDocs
        : prevRowCountState
    );
  }, [applications?.totalDocs, setRowCountState]);

  function handleGetAllApplications(userId, cursor) {
    dispatch(getAllApplications({ userId, cursor }));
  }

  useEffect(() => {
    if (user) handleGetAllApplications(user, page + 1 || 1);
  }, [user]);

  useEffect(() => {
    setRows(
      applications?.docs?.map((doc) => {
        const date = new Date(doc.createdAt);

        return {
          id: doc?._id,
          applicationId: doc?.applicationId,
          status: doc?.status,
          entityName: doc?.entities?.[0]?.entityName,
          firstName: doc.customers?.[0]?.firstName || "No",
          lastName: doc.customers?.[0]?.lastName || "customer",
          asset:
            doc?.asset?.make || doc.asset?.model
              ? `${doc?.asset?.ageOfAsset || ""} ${doc?.asset?.make || ""} ${
                  doc.asset?.model || ""
                }`
              : doc?.asset?.assetType,
          assetValue: doc?.asset?.assetValue,
          loanAmount: doc?.loanDetails?.loanAmount.toLocaleString(
            "en-US",
            dollarStringOptions
          ),
          brokerOrganisation: doc?.user?.organisation?.name,
          brokerContact: doc?.user?.firstName + " " + doc?.user?.lastName,
          applicationId: doc?.applicationId,
          startDate: `${date.getDate()}.${
            date.getMonth() + 1
          }.${date.getFullYear()}`,
          time: moment(doc?.createdAt).format("h:mm a"),
        };
      })
    );
  }, [applications]);

  return (
    <Grid
      container
      item
      direction="column"
      spacing={2}
      style={{ marginTop: "64px", padding: "20px" }}
    >
      <Grid item style={{ paddingTop: "0" }}>
        <Typography
          variant="h1"
          style={{ fontSize: "28px", fontWeight: 800, letterSpacing: "-1.5px" }}
        >
          Applications
        </Typography>
      </Grid>
      <Grid item>
        <DataGrid
          autoHeight
          autoPageSize
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onRowClick={(event) => {
            console.log(event);
            navigate(`/dashboard/application/${event.id}`);
          }}
          rows={rows || []}
          columns={
            user.userType === userTypes.MASTER ? masterColumns : brokerColumns
          }
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          style={{ background: "#ffffff" }}
          pagination
          pageSize={10}
          rowCount={rowCountState}
          paginationMode="server"
          onPageChange={handlePageChange}
          page={page}
          loading={isLoading}
        />
      </Grid>
    </Grid>
  );
}
